const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  media: '[data-product-media]',
  oos: '[data-product-oos]',
  price: '[data-product-price]',
  backInStockTrigger: '[data-product-back-in-stock-trigger]',
  preorderPopup: '[data-preorder-popup]',
  preorderPopupClose: '[data-preorder-popup-close]',
  preorderLearnMoreTrigger: 'purple-dot-learn-more span',
  apps: '[data-product-info-el="apps"]',
  stockBadge: '[data-product-stock-badge]',
  pdp: '[data-update-url="true"]',
  upsell: '[data-product-upsell]',
}

let skipNostoUpsellWatch = false
let nostoUpsellRequest = null

export class ProductInfo extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form']
  }

  static get requiredAttributes () {
    return ['data-section', 'data-product-info-id', 'data-url']
  }

  abortController = undefined
  pendingRequestUrl = undefined
  preReplaceProductOptionsCallbacks = []
  postReplacePageCallbacks = []

  checkValidity () {
    super.checkValidity()

    if (this.els.form.element.tagName !== 'PRODUCT-FORM') {
      this.throwError(`this.els.form.element should be a <product-form>`)
    }
  }

  connectedCallback () {
    super.connectedCallback()

    const selectedOption = this.querySelector(
      'product-options fieldset input:checked, product-options select option:checked',
    )

    if (selectedOption?.dataset.available === 'false') {
      this.syncCtaToSelectedOption(selectedOption)
    }

    setTimeout(() => {
      const variant = this.getSelectedVariant(this)

      if (variant?.available === false) {
        this.updateAvailability(false, window.theme.strings.soldOut)
        window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_CHANGED, {
          variant,
        })
      }
    }, 500)

    try {
      const appsEl = this.els.apps?.element

      if (appsEl) {
        this.appsResizeObserver = new ResizeObserver(([entry]) => {
          const height = entry.borderBoxSize
            ? entry.borderBoxSize[0].blockSize
            : entry.contentRect.height

          if (height > parseFloat(appsEl.style.minHeight || 0)) {
            appsEl.style.minHeight = `${height}px`
          }
        })

        this.appsResizeObserver.observe(appsEl)
      }
    } catch (error) {
      console.error(error)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onProductOptionChange = this.onProductOptionChange.bind(this)
    this.onProductOptionEntered = this.onProductOptionEntered.bind(this)
    this.onPopupCloseClick = this.onPopupCloseClick.bind(this)
    this.onPreorderLearnMoreClick = this.onPreorderLearnMoreClick.bind(this)
    this.nostoUpdate = this.nostoUpdate.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )
    this.addEventListener('click', this.onPopupCloseClick)
    this.addEventListener('click', this.onPreorderLearnMoreClick)

    this.nostoUpdate()
    this.watchNostoUpsell()

    this.postReplacePageCallbacks.push(() => {
      window.okeWidgetApi?.initAllWidgets?.()

      // Needed to trigger Order Deadline app again to update the product data and inject the app
      window.orderDeadlineAppApi?.init?.()
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.appsResizeObserver?.disconnect()
    this.appsNostoObserver?.disconnect()
    clearTimeout(this.nostoUpsellFallback)

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )
    this.removeEventListener('click', this.onPopupCloseClick)
    this.removeEventListener('click', this.onPreorderLearnMoreClick)
  }

  onPopupCloseClick (event) {
    const closeTrigger = event.target.closest(selectors.preorderPopupClose)
    if (!closeTrigger || !this.contains(closeTrigger)) return

    this.updatePreorderPopupState(false)
  }

  onPreorderLearnMoreClick (event) {
    const learnMoreTrigger = event.target.closest(
      selectors.preorderLearnMoreTrigger,
    )
    if (!learnMoreTrigger || !this.contains(learnMoreTrigger)) return

    this.updatePreorderPopupState(true)
  }

  nostoUpdate (e) {
    if (this.dataset.updateUrl === 'false') return
    // Only reload nosto when changing parent product-info

    document.body.dataset.productId = this.dataset.productId
  }

  watchNostoUpsell () {
    if (this.dataset.updateUrl === 'false') return
    if (skipNostoUpsellWatch) return

    const appsEl = this.els.apps?.element
    if (!appsEl) return
    if (appsEl.querySelector(selectors.upsell)) return

    this.appsNostoObserver = new MutationObserver(() => {
      if (!appsEl.querySelector(selectors.upsell)) return

      this.appsNostoObserver.disconnect()
      clearTimeout(this.nostoUpsellFallback)
    })

    this.appsNostoObserver.observe(appsEl, {
      childList: true,
      subtree: true,
    })

    this.nostoUpsellFallback = setTimeout(() => {
      this.appsNostoObserver.disconnect()
      this.ensureNostoUpsell()
    }, 2000)
  }

  get pdpProductInfo () {
    return window.BAO.Utils.getElement(selectors.pdp)
  }

  get pdpAppsEl () {
    return this.pdpProductInfo?.querySelector(selectors.apps)
  }

  getNostoPlacementIds (api, allPlacements = false) {
    const placementIds = api?.placements?.getPlacements() || []
    if (allPlacements) return placementIds

    const appsEl = this.pdpAppsEl

    return placementIds.filter((id) =>
      appsEl?.contains(document.getElementById(id)),
    )
  }

  ensureNostoUpsell () {
    if (this.dataset.updateUrl === 'false') return
    if (nostoUpsellRequest) return
    if (this.pdpAppsEl?.querySelector(selectors.upsell)) return

    this.reloadNostoRecommendations()
  }

  restoreUpsellHtml (appsHTML) {
    if (!appsHTML) return

    const appsEl = this.pdpAppsEl

    if (appsEl && !appsEl.querySelector(selectors.upsell)) {
      appsEl.innerHTML = appsHTML
    }
  }

  reloadNostoRecommendations (appsHTML, allPlacements = false) {
    if (this.dataset.updateUrl === 'false') return
    if (typeof window.nostojs !== 'function') return
    if (nostoUpsellRequest) return

    const productId = this.pdpProductInfo?.dataset.productId

    if (!productId) return

    document.body.dataset.productId = productId

    this.appsNostoObserver?.disconnect()
    clearTimeout(this.nostoUpsellFallback)

    nostoUpsellRequest = new Promise((resolve) => {
      window.nostojs((api) => {
        const placementIds = this.getNostoPlacementIds(api, allPlacements)

        if (!placementIds.length) {
          resolve()
          return
        }

        const request = api
          .defaultSession()
          .setResponseMode('HTML')
          .enableCampaignInjection()
          .viewProduct(productId)
          .setPlacements(placementIds)

        const pending =
          appsHTML && typeof request.update === 'function'
            ? request.update()
            : request.load()

        pending
          .catch((error) => {
            console.error('Failed to refresh Nosto placements:', error)
            this.restoreUpsellHtml(appsHTML)
          })
          .finally(resolve)
      })
    }).finally(() => {
      nostoUpsellRequest = null
    })
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {string[]} payload.detail.selectedOptionValues
   */
  onProductOptionEntered ({ detail: { event, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    const optionTarget = event.target.querySelector('[data-option-value-id]')
    if (!optionTarget) return
    if (!optionTarget.dataset.optionValueId) return

    const name = optionTarget.getAttribute('name')
    const selectedOption = this.querySelector(
      `[name="${name}"][selected], [name="${name}"]:checked`,
    )
    if (!selectedOption) return
    if (selectedOption === optionTarget) return

    const selectedOptionId = selectedOption.dataset.optionValueId
    if (!selectedOptionId) return

    const newSelectedOptionValues = selectedOptionValues.map((value) => {
      if (value === selectedOptionId) return optionTarget.dataset.optionValueId

      return value
    })

    const productUrl = optionTarget.dataset.productUrl || this.dataset.url
    fetchProductData(
      this.buildRequestUrlWithParams(productUrl, newSelectedOptionValues),
    )
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {HTMLOptionElement | EventTarget} payload.detail.target
   * @param {string[]} payload.detail.selectedOptionValues
   */
  async onProductOptionChange ({
    detail: { event, target, selectedOptionValues },
  }) {
    if (!this.contains(target)) return
    if (this.dataset.productInfoId !== target.dataset.productInfoId) return

    this.syncCtaToSelectedOption(target)
    this.loading = true

    const productUrl =
      target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
    this.pendingRequestUrl = productUrl
    const shouldSwapProduct = this.dataset.url !== productUrl
    const shouldFetchFullPage =
      this.dataset.updateUrl === 'true' && shouldSwapProduct

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
      ),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.renderProductDetails(productUrl),
    })
  }

  /**
   * @callback fetchProductDetailsCallback
   * @param {Document} html
   */

  /**
   * @param {Object} payload
   * @param {string} payload.requestUrl
   * @param {string} payload.targetId
   * @param {fetchProductDetailsCallback} payload.callback
   */
  async fetchProductDetails ({ requestUrl, targetId, callback }) {
    this.abortController?.abort()
    this.abortController = new AbortController()

    try {
      const text = await fetchProductData(
        requestUrl,
        this.abortController.signal,
      )

      this.pendingRequestUrl = undefined

      const html = new DOMParser().parseFromString(text, 'text/html')

      callback(html)

      window.BAO.Utils.getElement(`#${targetId}`)?.focus()
    } catch (e) {
      if (e.name === 'AbortError') {
        console.info('Fetch aborted by user')
      } else {
        console.error(e)
      }
    }
  }

  /**
   * @param {string} productUrl
   * @param {boolean} updateFullPage
   */
  handleSwapProduct (productUrl, updateFullPage = false) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      this.updateURL(productUrl, variant?.id)

      const appsHTML = this.els.apps?.element?.innerHTML

      skipNostoUpsellWatch = true

      if (updateFullPage) {
        document.querySelector('head title').innerHTML =
          html.querySelector('head title').innerHTML

        window.BAO.Utils.gracefullyReplaceNode(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      } else {
        window.BAO.Utils.gracefullyReplaceNode(
          this,
          html.querySelector('product-info'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      }

      skipNostoUpsellWatch = false

      this.restoreUpsellHtml(appsHTML)
      this.reloadNostoRecommendations(appsHTML, true)
    }
  }

  /**
   * @param {string} productUrl
   */
  renderProductDetails (productUrl) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      if (!variant) {
        this.updateAvailability(false, window.theme.strings.unavailable)
        this.updatePreorderClass(null, html)

        this.loading = false

        return
      }

      this.updateOptionValues(html)
      this.updateURL(productUrl, variant?.id)
      this.updateBackInStockTriggers(variant)

      const productIsAvailable =
        !this.isVariantSoldOut(variant?.id, html) &&
        !html
          .querySelector('product-form button[type=submit]')
          ?.hasAttribute('disabled')

      this.updateAvailability(
        productIsAvailable,
        this.getAddToCartText(productIsAvailable, variant?.id, html),
      )
      this.updatePreorderClass(variant?.id, html)

      /**
       * @callback PostReplaceCallback
       * @param {Element} currentElement
       * @param {Element} newElement
       */

      /**
       * @param {string} selector
       * @param {PostReplaceCallback} [postReplaceCallback]
       */
      const updateCurrentPageFromFetchedData = (
        selector,
        postReplaceCallback,
      ) => {
        const newElement = html.querySelector(selector)
        const currentElements = window.BAO.Utils.getElements(selector)

        if (newElement && currentElements) {
          currentElements.forEach((currentElement) => {
            if (currentElement.innerHTML !== newElement.innerHTML) {
              currentElement.innerHTML = newElement.innerHTML
            }
          })

          if (typeof postReplaceCallback === 'function') {
            postReplaceCallback(currentElements, newElement)
          }
        }
      }

      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.media}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.price}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.oos}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockTrigger}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.stockBadge}`,
      )

      this.updateVariantInputs(variant?.id)

      window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.OPTION_CHANGED, {
        sectionId: this.sectionId,
        html,
        variant,
      })

      this.ensureNostoUpsell()
      this.loading = false
    }
  }

  syncCtaToSelectedOption (optionEl) {
    if (!optionEl?.dataset?.available) return

    const isAvailable = optionEl.dataset.available !== 'false'
    const variantId = optionEl.dataset.variantId

    this.updateAvailability(
      isAvailable,
      this.getAddToCartText(isAvailable, variantId),
    )

    if (variantId) {
      this.updatePreorderClass(variantId)
    }
  }

  updateBackInStockTriggers (variant) {
    if (!variant) return

    const backInStockTriggerEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockTrigger}`,
    )

    if (!backInStockTriggerEls?.length) return

    backInStockTriggerEls.forEach((triggerEl) => {
      triggerEl.dataset.variantId = variant?.id || ''
      triggerEl.dataset.variantTitle = variant?.title || ''
    })
  }

  updateAvailability (productIsAvailable, text) {
    const addToCartEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )
    const addToCartTextEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartText}`,
    )
    const backInStockTriggerEls = window.BAO.Utils.getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockTrigger}`,
    )

    addToCartEls.forEach((addToCartEl) => {
      addToCartEl.toggleAttribute('disabled', !productIsAvailable)
      addToCartEl.setAttribute('aria-hidden', !productIsAvailable)
    })

    addToCartTextEls.forEach((addToCartTextEl) => {
      const resolvedText =
        text ||
        (productIsAvailable
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut)

      addToCartTextEl.setAttribute('data-text', resolvedText)

      if (addToCartTextEl.textContent.trim()) {
        addToCartTextEl.textContent = resolvedText
      }

      const buttonEl = addToCartTextEl.closest(selectors.addToCart)
      if (buttonEl) {
        buttonEl.setAttribute('aria-label', resolvedText)
      }
    })

    backInStockTriggerEls.forEach((backInStockTriggerEl) => {
      backInStockTriggerEl.setAttribute('aria-hidden', productIsAvailable)
    })
  }

  updatePreorderClass (variantId, root = this) {
    const isPreorder =
      this.isVariantOnPreorder(variantId, root) &&
      !this.isVariantSoldOut(variantId, root)

    this.productForms.forEach((productForm) => {
      const formEl =
        productForm.querySelector('form[data-push-cart-form]') ||
        productForm.querySelector('form')
      if (!formEl) return

      formEl.dataset.isPreorder = isPreorder ? 'true' : 'false'
      formEl.classList.toggle('is-preorder-variant', isPreorder)
      formEl.classList.toggle('x-pd-atc-form--preorder', isPreorder)
    })

    this.updatePreorderPopupState(false)
  }

  updatePreorderPopupState (isActive) {
    const preorderPopupEls = this.querySelectorAll(selectors.preorderPopup)

    preorderPopupEls.forEach((popupEl) => {
      popupEl.classList.toggle('prd-Detail_PreorderPopup-active', isActive)
      popupEl.setAttribute('aria-hidden', String(!isActive))
    })
  }

  getAddToCartText (productIsAvailable, variantId, root = this) {
    if (!productIsAvailable) {
      return window.theme.strings.soldOut
    }

    if (this.isVariantOnPreorder(variantId, root)) {
      return (
        window.variantStrings?.preOrder ||
        window.theme.strings.products?.product?.pre_order ||
        'Pre-order'
      )
    }

    return window.theme.strings.addToCart
  }

  getProductOptionsData (root, key) {
    const dataEl = root.querySelector(`[data-product-options-el="${key}"]`)
    if (!dataEl) return null

    try {
      return JSON.parse(dataEl.textContent)
    } catch (e) {
      return null
    }
  }

  isVariantOnPreorder (variantId, root = this) {
    return this.getVariantFlag(variantId, 'preorder-variants', root)
  }

  isVariantSoldOut (variantId, root = this) {
    return this.getVariantFlag(variantId, 'sold-out-variants', root)
  }

  getVariantFlag (variantId, identifier, root = this) {
    if (!variantId) return false

    const flagEl = root.querySelector(
      `${this.productOptions.tagName.toLowerCase()} [${this.productOptions._elIdentifier}="${identifier}"]`,
    )
    if (!flagEl) return false

    try {
      const flags = JSON.parse(flagEl.textContent)
      return Boolean(flags[String(variantId)])
    } catch (e) {
      return false
    }
  }

  areAllVariantsOnPreorder (root = this) {
    const preorderVariants = this.getProductOptionsData(
      root,
      'preorder-variants',
    )
    if (!preorderVariants) return false

    const values = Object.values(preorderVariants)

    return values.length > 0 && values.every(Boolean)
  }

  /**
   * @param {Document} html
   */
  updateOptionValues (html) {
    this.querySelectorAll('product-options').forEach((currentOption) => {
      if (!currentOption.id) return

      const incomingOption = html.getElementById(currentOption.id)
      if (!incomingOption || incomingOption.tagName !== 'PRODUCT-OPTIONS') {
        return
      }

      window.BAO.Utils.gracefullyReplaceNode(
        currentOption,
        incomingOption,
        this.preReplaceProductOptionsCallbacks,
      )
    })
  }

  /**
   * @param {string} url
   * @param {string} [variantId]
   */
  updateURL (url, variantId) {
    if (this.dataset.updateUrl === 'false') return

    const newUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`

    window.history.replaceState({}, '', newUrl)

    const shareButtons = window.BAO.Utils.getElements('share-button')

    if (!shareButtons) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(newUrl)
    })
  }

  /**
   * @param {string} [variantId]
   */
  updateVariantInputs (variantId) {
    if (!variantId) return

    this.productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')

      if (!input) return

      input.value = variantId
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  /**
   * @param {string} url
   * @param {string[]} optionValues
   * @param {boolean} shouldFetchFullPage
   */
  buildRequestUrlWithParams (url, optionValues, shouldFetchFullPage = false) {
    const params = []

    !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  /**
   * @param {Document} productInfoNode
   *
   * @returns {ShopifyVariant | null}
   */
  getSelectedVariant (productInfoNode) {
    const variant = this.getProductOptionsData(
      productInfoNode,
      'selected-variant',
    )

    if (variant && this.isVariantSoldOut(variant.id, productInfoNode)) {
      variant.available = false
    }

    return variant
  }

  get productForms () {
    return this.els.form.elements
  }

  get productInfoId () {
    return this.dataset.productInfoId
  }

  get productOptions () {
    return this.els.options.element
  }

  get sectionId () {
    return this.dataset.originalSection || this.dataset.section
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

class ProductInfoCache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  has (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

const cache = new ProductInfoCache()

/**
 * @param {string} requestUrl
 * @param {AbortSignal} abortSignal
 */
async function fetchProductData (requestUrl, abortSignal = undefined) {
  if (cache.has(requestUrl)) {
    return cache.get(requestUrl)
  }

  const response = await fetch(requestUrl, { signal: abortSignal })
  const text = await response.text()

  cache.set(requestUrl, text)

  return text
}
